import React, { useState } from 'react';
import { BudgetProgress } from '../components/BudgetProgress';
import { AutoAdjustmentSettings } from '../components/AutoAdjustmentSettings';
import { BudgetAlerts } from '../components/BudgetAlerts';
import { TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CheckCircleIcon, SettingsIcon } from 'lucide-react';
export function BudgetController() {
  const [monthlyBudget, setMonthlyBudget] = useState(1250000);
  const [currentUsage, setCurrentUsage] = useState(1053750);
  const [autoAdjustEnabled, setAutoAdjustEnabled] = useState(true);
  const budgetPercentage = currentUsage / monthlyBudget * 100;
  const remainingBudget = monthlyBudget - currentUsage;
  const daysInMonth = 30;
  const currentDay = 15;
  const dailyBudget = monthlyBudget / daysInMonth;
  const dailyUsage = currentUsage / currentDay;
  const projectedUsage = dailyUsage * daysInMonth;
  const weeklyData = [{
    week: 'Week 1',
    usage: 218250,
    budget: 312500
  }, {
    week: 'Week 2',
    usage: 262500,
    budget: 312500
  }, {
    week: 'Week 3',
    usage: 306000,
    budget: 312500
  }, {
    week: 'Week 4',
    usage: 267000,
    budget: 312500
  }];
  return <div className="p-6 max-w-7xl mx-auto" data-id="element-486">
      <header className="mb-8" data-id="element-487">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-488">
          Budget-Based Controller
        </h1>
        <p className="text-gray-500" data-id="element-489">
          Manage your energy consumption within your budget
        </p>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-490">
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-491">
          <div className="flex items-center justify-between mb-2" data-id="element-492">
            <p className="text-gray-500" data-id="element-493">Monthly Budget</p>
            <button className="text-blue-600 hover:text-blue-700" data-id="element-494">
              <SettingsIcon size={16} data-id="element-495" />
            </button>
          </div>
          <p className="text-3xl font-bold text-gray-800" data-id="element-496">
            {monthlyBudget.toLocaleString()}₫
          </p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-497">
            ~{(monthlyBudget / 25000).toFixed(1)} kWh
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-498">
          <p className="text-gray-500 mb-2" data-id="element-499">Current Usage</p>
          <div className="flex items-end gap-2" data-id="element-500">
            <p className="text-3xl font-bold text-gray-800" data-id="element-501">
              {currentUsage.toLocaleString()}₫
            </p>
            <span className={`text-sm flex items-center mb-1 ${budgetPercentage > 80 ? 'text-red-600' : 'text-green-600'}`} data-id="element-502">
              {budgetPercentage > 80 ? <TrendingUpIcon size={14} className="mr-1" data-id="element-503" /> : <TrendingDownIcon size={14} className="mr-1" data-id="element-504" />}
              {budgetPercentage.toFixed(0)}%
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1" data-id="element-505">Day {currentDay} of 30</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-506">
          <p className="text-gray-500 mb-2" data-id="element-507">Remaining Budget</p>
          <p className="text-3xl font-bold text-gray-800" data-id="element-508">
            {remainingBudget.toLocaleString()}₫
          </p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-509">
            ~{(remainingBudget / 25000 / (daysInMonth - currentDay)).toFixed(1)}{' '}
            kWh/day remaining
          </p>
        </div>
      </div>
      {budgetPercentage >= 80 && <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-start" data-id="element-510">
          <AlertCircleIcon className="text-yellow-500 mr-3 mt-0.5 flex-shrink-0" size={20} data-id="element-511" />
          <div className="flex-1" data-id="element-512">
            <h4 className="font-medium text-yellow-800 mb-1" data-id="element-513">
              Budget Alert: {budgetPercentage.toFixed(0)}% Used
            </h4>
            <p className="text-sm text-yellow-700 mb-2" data-id="element-514">
              You are approaching your monthly budget limit. At the current
              rate, you will exceed your budget by{' '}
              {(projectedUsage - monthlyBudget).toLocaleString()}₫.
            </p>
            {autoAdjustEnabled && <div className="flex items-center text-sm text-yellow-700" data-id="element-515">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-516" />
                Automatic adjustments are enabled to help you stay on track
              </div>}
          </div>
        </div>}
      <BudgetProgress currentUsage={currentUsage} monthlyBudget={monthlyBudget} currentDay={currentDay} daysInMonth={daysInMonth} data-id="element-517" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-518">
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-519">
          <h2 className="text-lg font-semibold mb-4" data-id="element-520">Weekly Breakdown</h2>
          <div className="space-y-4" data-id="element-521">
            {weeklyData.map((week, index) => {
            const weekPercentage = week.usage / week.budget * 100;
            return <div key={index} data-id="element-522">
                  <div className="flex justify-between mb-1" data-id="element-523">
                    <span className="text-sm text-gray-600" data-id="element-524">{week.week}</span>
                    <span className="text-sm font-medium" data-id="element-525">
                      {week.usage.toLocaleString()}₫ /{' '}
                      {week.budget.toLocaleString()}₫
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-526">
                    <div className={`h-2 rounded-full ${weekPercentage > 90 ? 'bg-red-500' : weekPercentage > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{
                  width: `${Math.min(weekPercentage, 100)}%`
                }} data-id="element-527"></div>
                  </div>
                </div>;
          })}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-528">
          <h2 className="text-lg font-semibold mb-4" data-id="element-529">Daily Usage Comparison</h2>
          <div className="space-y-4" data-id="element-530">
            <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-531">
              <span className="text-gray-600" data-id="element-532">Target Daily Budget</span>
              <span className="font-medium" data-id="element-533">
                {dailyBudget.toLocaleString()}₫/day
              </span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-534">
              <span className="text-gray-600" data-id="element-535">Actual Daily Average</span>
              <span className={`font-medium ${dailyUsage > dailyBudget ? 'text-red-600' : 'text-green-600'}`} data-id="element-536">
                {dailyUsage.toLocaleString()}₫/day
              </span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-100" data-id="element-537">
              <span className="text-gray-600" data-id="element-538">Projected Monthly Total</span>
              <span className={`font-medium ${projectedUsage > monthlyBudget ? 'text-red-600' : 'text-green-600'}`} data-id="element-539">
                {projectedUsage.toLocaleString()}₫
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-540">
              <span className="text-gray-600" data-id="element-541">Projected Over/Under</span>
              <span className={`font-medium ${projectedUsage > monthlyBudget ? 'text-red-600' : 'text-green-600'}`} data-id="element-542">
                {projectedUsage > monthlyBudget ? '+' : ''}
                {(projectedUsage - monthlyBudget).toLocaleString()}₫
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-543">
        <AutoAdjustmentSettings enabled={autoAdjustEnabled} onToggle={setAutoAdjustEnabled} data-id="element-544" />
        <BudgetAlerts data-id="element-545" />
      </div>
    </div>;
}